// GlobalDataUsagePage — Customer Success view of Global Data (zone lookup)
// usage across all customer accounts: grand totals plus a per-customer
// breakdown with per-zone detail. Data comes from CMAPI /global-data/usage,
// which proxies RisikaAPI /company/usage/global.
//
// Sorting (allocation, descending), pagination and search are all
// server-side: the table shows one page at a time and the search box
// queries across every page, not just the rows currently loaded.
//
// Semantics: each zone's usage counts its own countries independently
// (ENG2-2091), so a country shared between two zones counts toward both.

import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  faBuilding,
  faChartPie,
  faChevronDown,
  faChevronRight,
  faDatabase,
  faGlobeEurope,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import numeral from 'numeral'
import { Topbar } from 'layouts'
import { StatCard } from 'components/ui'
import { useGlobalDataUsage } from 'services/queries/use-global-data-usage'
import {
  CompanyGlobalDataUsage,
  GlobalDataUsageResponse,
} from 'services/cm-api/get-global-data-usage'
import { planLabel } from 'utils/plan-label'

const headerCellSx = {
  py: 1.5,
  px: 2.5,
  bgcolor: 'action.hover',
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const headerTypographySx = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'text.secondary',
}

const bodyCellSx = {
  py: 1.75,
  px: 2.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const formatNumber = (value: number): string => numeral(value).format('0,0')

const usagePercent = (usage: number, allocation: number): number | null => {
  if (allocation <= 0) return null
  return Math.min((usage / allocation) * 100, 100)
}

const usageColor = (
  usage: number,
  allocation: number,
): 'success' | 'warning' | 'error' => {
  if (allocation <= 0) return 'success'
  const ratio = usage / allocation
  if (ratio >= 0.95) return 'error'
  if (ratio >= 0.75) return 'warning'
  return 'success'
}

interface CompanyRowProps {
  company: CompanyGlobalDataUsage
}

const CompanyRow: React.FC<CompanyRowProps> = ({ company }) => {
  const [expanded, setExpanded] = useState(false)

  const remaining = company.total_allocation - company.total_usage
  const percent = usagePercent(company.total_usage, company.total_allocation)

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded((prev) => !prev)}
        sx={{ cursor: 'pointer' }}
        data-cy={`usage-row-${company.company_id}`}
      >
        <TableCell sx={{ ...bodyCellSx, width: 40, pr: 0 }}>
          <IconButton size="small" aria-label="Toggle zone breakdown">
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              style={{ fontSize: 12 }}
            />
          </IconButton>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {company.name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {company.country} · {company.local_id}
          </Typography>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <Chip label={planLabel(company.plan)} size="small" />
        </TableCell>
        <TableCell sx={bodyCellSx} align="right">
          <Typography sx={{ fontSize: 14 }}>
            {formatNumber(company.total_allocation)}
          </Typography>
        </TableCell>
        <TableCell sx={bodyCellSx} align="right">
          <Typography sx={{ fontSize: 14 }}>
            {formatNumber(company.total_usage)}
          </Typography>
        </TableCell>
        <TableCell sx={bodyCellSx} align="right">
          <Typography
            sx={{
              fontSize: 14,
              color: remaining < 0 ? 'error.main' : 'text.primary',
            }}
          >
            {formatNumber(remaining)}
          </Typography>
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, width: 180 }}>
          {percent === null ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              No allocation
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={percent}
                color={usageColor(
                  company.total_usage,
                  company.total_allocation,
                )}
                sx={{ flex: 1, height: 6, borderRadius: 3 }}
              />
              <Typography
                sx={{ fontSize: 12, color: 'text.secondary', width: 36 }}
              >
                {Math.round(
                  (company.total_usage / company.total_allocation) * 100,
                )}
                %
              </Typography>
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={7}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 7, py: 2, bgcolor: 'action.hover' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerTypographySx}>Zone</TableCell>
                    <TableCell sx={headerTypographySx}>Countries</TableCell>
                    <TableCell sx={headerTypographySx} align="right">
                      Allocation
                    </TableCell>
                    <TableCell sx={headerTypographySx} align="right">
                      Used
                    </TableCell>
                    <TableCell sx={headerTypographySx} align="right">
                      Remaining
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {company.zones.map((zone) => (
                    <TableRow key={zone.name}>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                        {zone.name}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {zone.countries.map((c) => c.toUpperCase()).join(', ')}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }} align="right">
                        {zone.allocation === null
                          ? '—'
                          : formatNumber(zone.allocation)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }} align="right">
                        {formatNumber(zone.usage)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }} align="right">
                        {zone.allocation === null
                          ? '—'
                          : formatNumber(zone.allocation - zone.usage)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
const SEARCH_DEBOUNCE_MS = 400

const GlobalDataUsagePage: React.FC = () => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  // Debounce the search input so we don't hit the API on every keystroke;
  // a new search always starts from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: response, isLoading } = useGlobalDataUsage({
    page,
    pageSize,
    q: search || undefined,
  })

  const usage: GlobalDataUsageResponse | null =
    response && response.success ? response.data : null

  // Rollout tolerance: a pre-pagination RisikaAPI returns every company in
  // one name-ordered response and ignores page/page_size/q. When the
  // pagination block is present we trust the server's shaping verbatim;
  // when it's absent we replicate the same contract client-side (search,
  // allocation-descending sort, slice) so the page behaves identically
  // against both backend versions.
  const { companies, pagination } = useMemo(() => {
    if (!usage) {
      return {
        companies: [] as GlobalDataUsageResponse['companies'],
        pagination: { page: 1, page_size: pageSize, total: 0, pages: 1 },
      }
    }
    if (usage.pagination) {
      return { companies: usage.companies, pagination: usage.pagination }
    }
    const query = search.toLowerCase()
    const filtered = query
      ? usage.companies.filter(
          (company) =>
            company.name.toLowerCase().includes(query) ||
            company.local_id.toLowerCase().includes(query),
        )
      : usage.companies
    const sorted = [...filtered].sort(
      (a, b) =>
        b.total_allocation - a.total_allocation || a.name.localeCompare(b.name),
    )
    const start = (page - 1) * pageSize
    return {
      companies: sorted.slice(start, start + pageSize),
      pagination: {
        page,
        page_size: pageSize,
        total: sorted.length,
        pages: Math.max(1, Math.ceil(sorted.length / pageSize)),
      },
    }
  }, [usage, search, page, pageSize])

  return (
    <>
      <Topbar title="Global Data Usage" />
      <Box sx={{ p: 3 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Loading global data usage…
            </Typography>
          </Box>
        ) : !usage ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>
              Could not load global data usage
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Try reloading the page. If the problem persists, check that
              CustomerManagementAPI can reach RisikaAPI.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Customers with Global Data"
                  value={formatNumber(usage.totals.companies)}
                  icon={<FontAwesomeIcon icon={faBuilding} />}
                  iconVariant="blue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Total allocation"
                  value={formatNumber(usage.totals.allocation)}
                  icon={<FontAwesomeIcon icon={faDatabase} />}
                  iconVariant="blue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Total used"
                  value={formatNumber(usage.totals.usage)}
                  icon={<FontAwesomeIcon icon={faChartPie} />}
                  iconVariant="orange"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Total remaining"
                  value={formatNumber(usage.totals.remaining)}
                  icon={<FontAwesomeIcon icon={faGlobeEurope} />}
                  iconVariant={usage.totals.remaining < 0 ? 'red' : 'green'}
                />
              </Grid>
            </Grid>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                  Usage per customer
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search all customers by name or local id…"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  sx={{ width: 300 }}
                  data-cy="usage-search"
                />
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, width: 40 }} />
                      <TableCell sx={headerCellSx}>
                        <Typography sx={headerTypographySx}>
                          Customer
                        </Typography>
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        <Typography sx={headerTypographySx}>Plan</Typography>
                      </TableCell>
                      <TableCell sx={headerCellSx} align="right">
                        <Typography sx={headerTypographySx}>
                          Allocation
                        </Typography>
                      </TableCell>
                      <TableCell sx={headerCellSx} align="right">
                        <Typography sx={headerTypographySx}>Used</Typography>
                      </TableCell>
                      <TableCell sx={headerCellSx} align="right">
                        <Typography sx={headerTypographySx}>
                          Remaining
                        </Typography>
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        <Typography sx={headerTypographySx}>Usage</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companies.length === 0 ? (
                      <TableRow>
                        <TableCell sx={bodyCellSx} colSpan={7}>
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography
                              sx={{ fontSize: 14, color: 'text.secondary' }}
                            >
                              {search
                                ? 'No customers match the search'
                                : 'No customers have Global Data enabled'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      companies.map((company) => (
                        <CompanyRow
                          key={company.company_id}
                          company={company}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={pagination.total}
                page={page - 1}
                onPageChange={(_event, newPage) => setPage(newPage + 1)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  setPageSize(parseInt(event.target.value, 10))
                  setPage(1)
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                data-cy="usage-pagination"
                // Left-align the controls (MUI right-aligns them via a flex
                // spacer) so they stay clear of the HubSpot chat bubble fixed
                // to the viewport's bottom-right corner
                sx={{ '& .MuiTablePagination-spacer': { display: 'none' } }}
              />
            </Paper>
          </>
        )}
      </Box>
    </>
  )
}

export { GlobalDataUsagePage }
export default GlobalDataUsagePage
